import React from 'react';
import { fmtMoney, fmtDate } from '../money';

// A single OL Finance report renderer that formats EVERY olfin report shape into a
// financial document: header, section totals, formatted money, clickable amounts.
// No JSON.stringify anywhere — unknown shapes fall through to a formatted table.

const HIDE = new Set(['account_id', 'txn_id', 'name_id', 'vendor_id', 'customer_id', 'id', 'void',
  'pay_txn', 'charge_txn', 'class_id', 'item_id', 'payee_id', 'deposit_to_id', 'due_days', 'type_order']);
const NONMONEY = new Set(['year', 'tax_year', 'fiscal_year', 'count', 'qty', 'box', 'days', 'number', 'num', 'ref', 'pct', 'bucket', 'name', 'display_name', 'company', 'date', 'due_date', 'memo', 'type', 'cleared', 'full_name', 'tax_id_masked']);
const MONEYHINT = /(amount|balance|total|debit|credit|open|current|prior|change|opening|closing|movement|budget|actual|variance|owed|paid|net|cash|sub|over_90|1-30|31-60|61-90)/i;

const isMoneyKey = (k) => !NONMONEY.has(k) && MONEYHINT.test(k);
const human = (k) => k.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

function cellText(k, v) {
  if (v == null || v === '') return '';
  if (k === 'pct') return `${v}%`;
  if (typeof v === 'boolean') return v ? 'Yes' : '';
  if (typeof v === 'number' && Number.isInteger(v) && isMoneyKey(k)) return fmtMoney(v);
  if ((k === 'date' || k === 'due_date') && /^\d{4}-\d{2}-\d{2}/.test(String(v))) return fmtDate(v);
  return String(v);
}

const num = (v) => (typeof v === 'number' ? v : 0);
// leaf numbers print WITHOUT $ (negatives as -1,234.56); subtotals/totals WITH $.
const fmtNum = (c) => {
  const n = (c || 0) / 100;
  const s = Math.abs(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return n < 0 ? `-${s}` : s;
};
const Amt = ({ c, onClick, bold }) => (
  <span onClick={onClick}
    className={`tabular-nums ${onClick ? 'cursor-pointer hover:text-emerald-500 hover:underline' : ''} ${bold ? 'font-semibold' : ''}`}>
    {fmtMoney(c)}
  </span>
);

// Build an account tree from "Parent:Child" full names; subtotal = own + descendants.
function buildTree(rows) {
  const root = { children: {}, order: [], self: null };
  rows.forEach((r) => {
    const parts = String(r.name || '').split(':');
    let node = root;
    parts.forEach((seg, idx) => {
      if (!node.children[seg]) { node.children[seg] = { seg, children: {}, order: [], self: null, path: parts.slice(0, idx + 1).join(':') }; node.order.push(seg); }
      node = node.children[seg];
    });
    node.self = r;
  });
  return root;
}
function subtotals(node, ncols) {
  const t = new Array(ncols).fill(0);
  if (node.self) node.self.amounts.forEach((a, i) => { t[i] += num(a); });
  node.order.forEach((k) => { subtotals(node.children[k], ncols).forEach((a, i) => { t[i] += a; }); });
  return t;
}

export function ReportDocument({ rep, title, company, periodLabel, basis, compact = false,
  names = {}, onDrill, onDrillLine, onOpenTxn, sections = {}, netLbls = null, showAllRows = false, style = null }) {
  if (!rep) return null;
  const pad = compact ? 'py-0.5' : 'py-1.5';
  const line = 'border-slate-200 dark:border-slate-700';
  const sec = (type) => sections[type] || type || 'Other';
  const NL = netLbls || { grossProfit: 'Gross Profit', netOperating: 'Net Operating Income', net: 'Net Income' };
  const TORDER = ['Income', 'Cost of Goods Sold', 'Expense', 'Other Income', 'Other Expense',
    'Bank', 'Accounts Receivable', 'Other Current Asset', 'Fixed Asset', 'Other Asset',
    'Accounts Payable', 'Credit Card', 'Other Current Liability', 'Long Term Liability', 'Equity'];
  const allZero = (vals) => vals.every((v) => !num(v));
  const blanks = (nc) => new Array(nc).fill('');
  const body = (() => {
    const rows = rep.rows;
    const isBS = (rep.report || '').startsWith('balance_sheet');
    // Budget vs. Actuals / Budget Overview (budget bridge): sections ▸ lines, per column group
    // Actual · Budget · $ Over Budget · % of Budget (Overview: Budget only); cents.
    if ((rep.report === 'budget_vs_actual' || rep.report === 'budget_overview') && Array.isArray(rep.sections)) {
      const ov = rep.report === 'budget_overview';
      const cols = rep.columns || ['Total'];
      const per = ov ? 1 : 4;
      const pctTxt = (v) => (v.pct == null ? '' : `${v.pct}%`);
      const Vals = ({ vals, bold }) => vals.map((v, i) => (ov
        ? <td key={i} className={`${pad} px-3 text-right tabular-nums`}>{bold ? <Amt c={v.budget} bold /> : fmtNum(v.budget)}</td>
        : <React.Fragment key={i}>
            <td className={`${pad} px-3 text-right tabular-nums`}>{bold ? <Amt c={v.actual} bold /> : fmtNum(v.actual)}</td>
            <td className={`${pad} px-3 text-right tabular-nums`}>{bold ? <Amt c={v.budget} bold /> : fmtNum(v.budget)}</td>
            <td className={`${pad} px-3 text-right tabular-nums`}>{bold ? <Amt c={v.over} bold /> : fmtNum(v.over)}</td>
            <td className={`${pad} px-3 text-right tabular-nums`}>{pctTxt(v)}</td>
          </React.Fragment>));
      if (!rep.sections.length) return <p className="text-sm text-slate-500" data-testid="report-empty">{rep.empty || "Your selection doesn't have any info."}</p>;
      return (
        <table className="w-full text-sm" data-testid="budget-report">
          <thead>
            {cols.length > 1 && <tr className="text-slate-500"><th />{cols.map((c) => <th key={c} colSpan={per} className={`${pad} px-3 text-center border-b ${line}`}>{c}</th>)}</tr>}
            <tr className="text-right text-slate-500"><th className={`text-left ${pad} pl-1`} />
              {cols.map((c) => (ov ? <th key={c} className={`${pad} px-3`}>Budget</th>
                : <React.Fragment key={c}><th className={`${pad} px-3`}>Actual</th><th className={`${pad} px-3`}>Budget</th><th className={`${pad} px-3`}>$ Over Budget</th><th className={`${pad} px-3`}>% of Budget</th></React.Fragment>))}
            </tr>
          </thead>
          <tbody>
            {rep.sections.map((s) => (
              <React.Fragment key={`${s.kind}-${s.name}`}>
                <tr><td className={`${pad} font-semibold`} colSpan={1 + cols.length * per}>{s.name}</td></tr>
                {s.lines.map((l) => (
                  <tr key={l.line_id} className={`border-b ${line}`}>
                    <td className={`${pad}`} style={{ paddingLeft: '1.35rem' }}>
                      {!ov && l.account_ids?.length && onDrillLine
                        ? <span className="cursor-pointer hover:text-emerald-500 hover:underline" onClick={() => onDrillLine(l.account_ids)} title="Show the transactions">{l.name}</span>
                        : <span title={!ov && !l.linked ? 'Not linked to an account — actual typed in AD Finance' : ''}>{l.name}{!ov && !l.linked ? ' *' : ''}</span>}
                    </td>
                    <Vals vals={l.values} />
                  </tr>
                ))}
                <tr className={`border-b-2 ${line}`}><td className={`${pad} font-semibold`}>Total {s.name}</td><Vals vals={s.subtotal} bold /></tr>
              </React.Fragment>
            ))}
            <tr className="border-t-2 border-slate-400"><td className={`${pad} font-bold`}>Net</td><Vals vals={rep.net || []} bold /></tr>
          </tbody>
          {!ov && rep.sections.some((s) => s.lines.some((l) => !l.linked)) && (
            <tfoot><tr><td colSpan={1 + cols.length * per} className="pt-2 text-xs text-slate-500">* Not linked to an OL Finance account — the actual is the figure typed in AD Finance ▸ Budget.</td></tr></tfoot>)}
        </table>
      );
    }
    // Income by Customer: flat 3-column (Income | Expenses | Net income) + totals
    if (rep.report === 'income_by_customer_summary') {
      const cols = rep.columns || ['Income', 'Expenses', 'Net income'];
      const t = rep.totals || {};
      const totRow = [t.income, t.expenses, t.net];
      return (
        <table className="w-full text-sm">
          <thead><tr className="text-right text-slate-500"><th className={`text-left ${pad} pl-1`}>Customer</th>{cols.map((c) => <th key={c} className={`${pad} px-3`}>{c}</th>)}</tr></thead>
          <tbody>
            {(rows || []).map((r, i) => (
              <tr key={r.name || i} className={`border-b ${line}`} data-testid={`inc-cust-row-${i}`}>
                <td className={`${pad} pl-1`}>{r.name}</td>
                {r.amounts.map((a, j) => <td key={j} className={`${pad} px-3 text-right tabular-nums`}>{fmtNum(a)}</td>)}
              </tr>
            ))}
            <tr className="border-t-2 border-slate-400">
              <td className={`${pad} pl-1 font-bold`}>Total</td>
              {totRow.map((a, j) => <td key={j} className={`${pad} px-3 text-right`}><Amt c={a || 0} bold /></td>)}
            </tr>
          </tbody>
        </table>
      );
    }
    // 1) Columnar account report (SoA/SoFP): rows carry amounts[], rendered as a tree
    if (Array.isArray(rows) && rows.length && Array.isArray(rows[0].amounts)) {
      let cols = rep.columns || ['Total'];
      if (isBS) cols = cols.map((c) => {
        const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(c));
        if (!m) return c;  // e.g. "All Dates" / "$ Change"
        const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
        return `As of ${d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}`;
      });
      const nc = cols.length;
      const byType = {};
      rows.forEach((r) => { (byType[r.type || ''] = byType[r.type || ''] || []).push(r); });

      const RowCells = ({ label, vals, depth, bold, big, drillId }) => (
        <tr className={`${bold ? `border-b-2 ${line}` : `border-b ${line}`} ${big ? 'border-slate-400' : ''}`}>
          <td className={`${pad} ${bold ? 'font-semibold' : ''}`} style={{ paddingLeft: `${0.25 + depth * 1.1}rem` }}>{label}</td>
          {vals.map((a, i) => (
            <td key={i} className={`${pad} px-3 text-right`}>
              {a === '' ? '' : (bold ? <Amt c={a} bold /> : <span className="tabular-nums cursor-pointer hover:text-emerald-500 hover:underline" onClick={drillId && onDrill ? () => onDrill(drillId) : undefined}>{fmtNum(a)}</span>)}
            </td>
          ))}
        </tr>
      );
      // prune all-zero subtrees unless "Show all rows"
      const keep = (node) => {
        if (showAllRows) return true;
        node.order = node.order.filter((k) => keep(node.children[k]));
        // keep an account that had activity in the period even if its balance is 0.00
        const selfNZ = node.self && (!allZero(node.self.amounts) || node.self.has_activity);
        return node.order.length > 0 || selfNZ;
      };
      // parent's own amount sits ON the parent row — no duplicate child row repeating the name.
      const renderNode = (node, depth, acc) => {
        const kids = node.order;
        const selfVals = node.self ? node.self.amounts : blanks(nc);
        if (!kids.length) {
          acc.push(<RowCells key={node.path} label={node.seg} vals={selfVals} depth={depth} drillId={node.self && node.self.account_id} />);
          return;
        }
        acc.push(<RowCells key={`${node.path}-h`} label={node.seg} vals={selfVals} depth={depth} drillId={node.self && node.self.account_id} />);
        kids.forEach((k) => renderNode(node.children[k], depth + 1, acc));
        acc.push(<RowCells key={`${node.path}-t`} label={`Total for ${node.seg}`} vals={subtotals(node, nc)} depth={depth} bold />);
      };
      const typeRows = (type, depth, acc) => {
        const rs = byType[type] || [];
        if (!rs.length) return new Array(nc).fill(0);
        const tree = buildTree(rs);
        if (!showAllRows) tree.order = tree.order.filter((k) => keep(tree.children[k]));
        const a = [];
        tree.order.forEach((k) => renderNode(tree.children[k], depth, a));
        a.forEach((el) => acc.push(el));
        return subtotals(tree, nc);
      };
      const sumv = (...arrs) => { const t = new Array(nc).fill(0); arrs.forEach((x) => x.forEach((v, i) => { t[i] += num(v); })); return t; };
      const has = (t) => !!(byType[t] || []).length;
      const Hdr = (label, depth, k) => out.push(<tr key={`hdr-${k}`}><td className={`${pad} font-semibold text-slate-700 dark:text-slate-200`} style={{ paddingLeft: `${0.25 + depth * 1.1}rem` }} colSpan={nc + 1}>{label}</td></tr>);
      const out = [];

      if (isBS) {
        // ── Statement of Financial Position (strict legacy layout) ──
        const SUB = { Bank: 'Bank Accounts', 'Accounts Receivable': 'Accounts Receivable', 'Other Current Asset': 'Other Current Assets',
          'Fixed Asset': 'Fixed Assets', 'Other Asset': 'Other Assets',
          'Accounts Payable': 'Accounts Payable', 'Credit Card': 'Credit Cards', 'Other Current Liability': 'Other Current Liabilities',
          'Long Term Liability': 'Long-Term Liabilities', 'Other Liability': 'Other Liabilities' };
        Hdr('ASSETS', 0, 'assets');
        Hdr('Current Assets', 1, 'ca');
        let caT = new Array(nc).fill(0);
        ['Bank', 'Accounts Receivable', 'Other Current Asset'].forEach((t) => {
          if (!has(t)) return;
          Hdr(SUB[t], 2, `casub-${t}`);
          const st = typeRows(t, 3, out);
          out.push(<RowCells key={`ca-${t}`} label={`Total ${SUB[t]}`} vals={st} depth={2} bold />);
          caT = sumv(caT, st);
        });
        out.push(<RowCells key="tca" label="Total Current Assets" vals={caT} depth={1} bold />);
        let assetT = sumv(caT);
        ['Fixed Asset', 'Other Asset'].forEach((t) => {
          if (!has(t)) return;
          Hdr(SUB[t], 1, `ncsub-${t}`);
          const st = typeRows(t, 2, out);
          out.push(<RowCells key={`nc-${t}`} label={`Total ${SUB[t]}`} vals={st} depth={1} bold />);
          assetT = sumv(assetT, st);
        });
        out.push(<RowCells key="ta" label="TOTAL ASSETS" vals={assetT} depth={0} bold big />);

        Hdr('LIABILITIES AND EQUITY', 0, 'le');
        Hdr('Liabilities', 1, 'liab');
        let liabT = new Array(nc).fill(0);
        if (['Accounts Payable', 'Credit Card', 'Other Current Liability'].some(has)) {
          Hdr('Current Liabilities', 2, 'cl');
          let clT = new Array(nc).fill(0);
          ['Accounts Payable', 'Credit Card', 'Other Current Liability'].forEach((t) => {
            if (!has(t)) return;
            Hdr(SUB[t], 3, `clsub-${t}`);
            const st = typeRows(t, 4, out);
            out.push(<RowCells key={`cl-${t}`} label={`Total ${SUB[t]}`} vals={st} depth={3} bold />);
            clT = sumv(clT, st);
          });
          out.push(<RowCells key="tcl" label="Total Current Liabilities" vals={clT} depth={2} bold />);
          liabT = sumv(liabT, clT);
        }
        ['Long Term Liability', 'Other Liability'].forEach((t) => {
          if (!has(t)) return;
          Hdr(SUB[t], 2, `llsub-${t}`);
          const st = typeRows(t, 3, out);
          out.push(<RowCells key={`ll-${t}`} label={`Total ${SUB[t]}`} vals={st} depth={2} bold />);
          liabT = sumv(liabT, st);
        });
        out.push(<RowCells key="tl" label="Total Liabilities" vals={liabT} depth={1} bold />);

        Hdr('Equity', 1, 'equity');
        let eqT = typeRows('Equity', 2, out);
        const niBS = rep.net_income == null ? null : (Array.isArray(rep.net_income) ? rep.net_income : [rep.net_income]);
        if (niBS) {
          const niv = new Array(nc).fill(0).map((_, i) => num(niBS[i] != null ? niBS[i] : niBS[0]));
          out.push(<RowCells key="netrev" label={NL.net} vals={niv} depth={2} />);
          eqT = sumv(eqT, niv);
        }
        out.push(<RowCells key="teq" label="Total Equity" vals={eqT} depth={1} bold />);
        out.push(<RowCells key="tle" label="TOTAL LIABILITIES AND EQUITY" vals={sumv(liabT, eqT)} depth={0} bold big />);
        return (
          <table className="w-full text-sm">
            <thead><tr className="text-right text-slate-500"><th className={`text-left ${pad} pl-1`}> </th>{cols.map((c) => <th key={c} className={`${pad} px-3`}>{c}</th>)}</tr></thead>
            <tbody>{out}</tbody>
          </table>
        );
      }

      // ── P&L / Statement of Activity ──
      const groups = Object.keys(byType).map((t) => ({ type: t }));
      groups.sort((a, b) => (TORDER.indexOf(a.type) + 1 || 99) - (TORDER.indexOf(b.type) + 1 || 99));
      const ni = rep.net_income == null ? null : (Array.isArray(rep.net_income) ? rep.net_income : [rep.net_income]);
      const PL = (rep.report || '').startsWith('profit_loss');
      const colSum = (t) => subtotals(buildTree(byType[t] || []), nc);
      const incT = colSum('Income'); const cogsT = colSum('Cost of Goods Sold'); const expT = colSum('Expense');
      const hasCOGS = has('Cost of Goods Sold');
      const hasOther = has('Other Income') || has('Other Expense');
      groups.forEach((g) => {
        const label = sec(g.type);
        out.push(<tr key={`${g.type}-sec`}><td className={`${pad} pl-1 font-semibold text-slate-700 dark:text-slate-200`} colSpan={nc + 1}>{label}</td></tr>);
        const st = typeRows(g.type, 1, out);
        out.push(<RowCells key={`${g.type}-tot`} label={`Total for ${label}`} vals={st} depth={0} bold />);
        if (PL && hasCOGS && g.type === 'Cost of Goods Sold') out.push(<RowCells key="gp" label={NL.grossProfit} vals={incT.map((v, i) => v - cogsT[i])} depth={0} bold big />);
        if (PL && hasOther && g.type === 'Expense') out.push(<RowCells key="noi" label={NL.netOperating} vals={incT.map((v, i) => v - cogsT[i] - expT[i])} depth={0} bold big />);
      });
      return (
        <table className="w-full text-sm">
          <thead><tr className="text-right text-slate-500"><th className={`text-left ${pad} pl-1`}> </th>{cols.map((c) => <th key={c} className={`${pad} px-3`}>{c}</th>)}</tr></thead>
          <tbody>
            {out}
            {ni && (
              <tr className="border-t-2 border-slate-400"><td className={`${pad} pl-1 font-bold`}>{NL.net}</td>{ni.map((n, i) => <td key={i} className={`${pad} px-3 text-right`}><Amt c={n} bold /></td>)}</tr>
            )}
          </tbody>
        </table>
      );
    }
    // 2) Prev-year comparison: current / prior / change / pct
    if (Array.isArray(rows) && rows.length && 'current' in rows[0] && 'prior' in rows[0]) {
      const cols = rep.columns || ['Current', 'Prior', '$ Change', '% Change'];
      return (
        <table className="w-full text-sm">
          <thead><tr className="text-right text-slate-500"><th className={`text-left ${pad} pl-1`}> </th>{cols.map((c) => <th key={c} className={`${pad} px-3`}>{c}</th>)}</tr></thead>
          <tbody>{rows.map((r) => (
            <tr key={r.name} className={`border-b ${line}`}>
              <td className={`${pad} pl-3`}>{r.name}</td>
              <td className={`${pad} px-3 text-right`}><Amt c={r.current} /></td>
              <td className={`${pad} px-3 text-right`}><Amt c={r.prior} /></td>
              <td className={`${pad} px-3 text-right`}><Amt c={r.change} /></td>
              <td className={`${pad} px-3 text-right tabular-nums`}>{r.pct == null ? '—' : `${r.pct}%`}</td>
            </tr>
          ))}</tbody>
        </table>
      );
    }
    // 3) Statement of Cash Flows
    if (rep.operating || rep.operating_total != null) {
      const Section = ({ label, items, total }) => (
        <>
          <tr><td className={`${pad} pl-1 font-semibold`} colSpan={2}>{label}</td></tr>
          {(items || []).map((x, i) => (
            <tr key={i} className={`border-b ${line}`}><td className={`${pad} pl-5`}>{x.name}</td><td className={`${pad} px-3 text-right`}><Amt c={x.amount} /></td></tr>
          ))}
          <tr className={`border-b-2 ${line}`}><td className={`${pad} pl-3 font-semibold`}>Total {label}</td><td className={`${pad} px-3 text-right`}><Amt c={total} bold /></td></tr>
        </>
      );
      return (
        <table className="w-full text-sm"><tbody>
          <tr className={`border-b ${line}`}><td className={`${pad} pl-5`}>Net Income</td><td className={`${pad} px-3 text-right`}><Amt c={rep.net_income} /></td></tr>
          <Section label="Operating Activities" items={rep.operating} total={rep.operating_total} />
          <Section label="Investing Activities" items={rep.investing} total={rep.investing_total} />
          <Section label="Financing Activities" items={rep.financing} total={rep.financing_total} />
          <tr className="border-t-2 border-slate-400"><td className={`${pad} pl-1 font-bold`}>Net Change in Cash</td><td className={`${pad} px-3 text-right`}><Amt c={rep.net_change_in_cash} bold /></td></tr>
        </tbody></table>
      );
    }
    // 4) General Ledger (per-account groups with lines + running balance)
    if (rep.report === 'general_ledger' && Array.isArray(rows)) {
      return (
        <div className="space-y-4">{rows.map((acc) => (
          <div key={acc.account_id}>
            <div className="font-semibold text-slate-700 dark:text-slate-200">{acc.name}</div>
            <table className="w-full text-sm"><thead><tr className="text-left text-slate-500">
              <th className={`${pad} pl-3`}>Date</th><th className={pad}>Memo</th>
              <th className={`${pad} text-right px-3`}>Debit</th><th className={`${pad} text-right px-3`}>Credit</th><th className={`${pad} text-right px-3`}>Balance</th>
            </tr></thead><tbody>
              <tr className={`border-b ${line} text-slate-500`}><td className={`${pad} pl-3`} colSpan={4}>Beginning balance</td><td className={`${pad} px-3 text-right`}><Amt c={acc.opening} /></td></tr>
              {acc.lines.map((l, i) => (
                <tr key={i} className={`border-b ${line} ${onOpenTxn ? 'cursor-pointer hover:bg-emerald-500/5' : ''}`} onClick={onOpenTxn ? () => onOpenTxn(l.txn_id) : null}>
                  <td className={`${pad} pl-3`}>{fmtDate(l.date)}</td><td className={pad}>{l.memo || ''}</td>
                  <td className={`${pad} px-3 text-right`}>{l.debit ? fmtMoney(l.debit) : ''}</td>
                  <td className={`${pad} px-3 text-right`}>{l.credit ? fmtMoney(l.credit) : ''}</td>
                  <td className={`${pad} px-3 text-right`}><Amt c={l.balance} /></td>
                </tr>
              ))}
              <tr className="border-t-2 border-slate-400"><td className={`${pad} pl-3 font-semibold`} colSpan={4}>Total {acc.name}</td><td className={`${pad} px-3 text-right`}><Amt c={acc.closing} bold /></td></tr>
            </tbody></table>
          </div>
        ))}</div>
      );
    }
    // 5) Journal (grouped transactions with splits)
    if (rep.report === 'journal' && Array.isArray(rows)) {
      return (
        <table className="w-full text-sm"><thead><tr className="text-left text-slate-500">
          <th className={`${pad} pl-3`}>Date</th><th className={pad}>Type</th><th className={pad}>Num</th><th className={pad}>Account</th>
          <th className={`${pad} text-right px-3`}>Debit</th><th className={`${pad} text-right px-3`}>Credit</th>
        </tr></thead><tbody>
          {rows.map((t) => (
            <React.Fragment key={t.txn_id}>
              {t.lines.map((l, i) => (
                <tr key={i} className={`border-b ${line} ${onOpenTxn ? 'cursor-pointer hover:bg-emerald-500/5' : ''}`} onClick={onOpenTxn ? () => onOpenTxn(t.txn_id) : null}>
                  <td className={`${pad} pl-3`}>{i === 0 ? fmtDate(t.date) : ''}</td>
                  <td className={pad}>{i === 0 ? t.type : ''}</td>
                  <td className={pad}>{i === 0 ? (t.num || '') : ''}</td>
                  <td className={pad}>{names[l.account_id] || l.account_id}</td>
                  <td className={`${pad} px-3 text-right`}>{l.debit ? fmtMoney(l.debit) : ''}</td>
                  <td className={`${pad} px-3 text-right`}>{l.credit ? fmtMoney(l.credit) : ''}</td>
                </tr>
              ))}
            </React.Fragment>
          ))}
        </tbody></table>
      );
    }
    // 6) Generic flat table (aging, balances, detail, 1099, audit, TB…)
    if (Array.isArray(rows) && rows.length) {
      const keys = [];
      rows.forEach((r) => Object.keys(r).forEach((k) => {
        if (!HIDE.has(k) && !keys.includes(k) && !Array.isArray(r[k]) && typeof r[k] !== 'object') keys.push(k);
      }));
      const totals = rep.totals || null;
      // a report may name its money columns and headings (nonprofit reports); otherwise guess from the key
      const mk = rep.money_keys ? new Set(rep.money_keys) : null;
      const isMoneyKeyR = (k) => (mk ? mk.has(k) : isMoneyKey(k));
      const head = (k) => (rep.labels && rep.labels[k]) || human(k);
      const cellR = (k, v) => (mk && mk.has(k) && typeof v === 'number' ? fmtNum(v) : cellText(k, v));
      return (
        <>
        <table className="w-full text-sm">
          <thead><tr className="text-left text-slate-500">
            {keys.map((k) => <th key={k} className={`${pad} px-3 ${isMoneyKeyR(k) ? 'text-right' : ''}`}>{head(k)}</th>)}
          </tr></thead>
          <tbody>
            {rows.map((r, idx) => (
              <tr key={r.txn_id || idx} className={`border-b ${line} ${r.txn_id && onOpenTxn ? 'cursor-pointer hover:bg-emerald-500/5' : ''}`}
                onClick={r.txn_id && onOpenTxn ? () => onOpenTxn(r.txn_id) : null}>
                {keys.map((k) => (
                  <td key={k} className={`${pad} px-3 ${isMoneyKeyR(k) ? 'text-right tabular-nums' : ''}`}>{cellR(k, r[k])}</td>
                ))}
              </tr>
            ))}
            {totals && (
              <tr className="border-t-2 border-slate-400 font-semibold">
                {keys.map((k, i) => <td key={k} className={`${pad} px-3 ${isMoneyKeyR(k) ? 'text-right tabular-nums' : ''}`}>{i === 0 ? 'Total' : (totals[k] != null ? fmtMoney(totals[k]) : '')}</td>)}
              </tr>
            )}
          </tbody>
        </table>
        {(rep.notes || []).map((n) => <p key={n} className="mt-2 text-xs text-slate-500">{n}</p>)}
        </>
      );
    }
    return <p className="text-sm text-slate-500" data-testid="report-empty">Your selection doesn't have any info.</p>;
  })();

  const grand = rep.total != null && !rep.totals ? rep.total : (rep.net_change_in_cash != null && rep.operating == null ? rep.net_change_in_cash : null);
  const vis = (style && style.visual) || {};
  const styleCls = [
    vis.banded_rows !== false ? '[&_tbody_tr:nth-child(even)]:bg-slate-50 dark:[&_tbody_tr:nth-child(even)]:bg-slate-800/40' : '',
    vis.gridlines ? '[&_td]:border [&_th]:border [&_td]:border-slate-200 [&_th]:border-slate-200 dark:[&_td]:border-slate-700 dark:[&_th]:border-slate-700' : '',
  ].join(' ');
  return (
    <div data-testid="report-document" className={`bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 rounded-md p-5 print:p-0 ${styleCls}`}>
      {vis.header !== false && (
      <div className="text-center mb-4">
        <div className="text-base font-semibold">{company || ''}</div>
        <div className="text-lg font-bold">{title}</div>
        <div className="text-xs text-slate-500">{periodLabel}{basis ? ` · ${basis === 'cash' ? 'Cash' : 'Accrual'} basis` : ''}</div>
      </div>
      )}
      {body}
      {grand != null && (
        <div className="flex justify-between border-t-2 border-slate-400 mt-1 pt-1 font-bold text-sm">
          <span>Total</span><span className="tabular-nums">{fmtMoney(grand)}</span>
        </div>
      )}
      {vis.footer && vis.footer !== true && <div className="text-center text-[11px] text-slate-400 border-t border-slate-200 mt-3 pt-1">{typeof vis.footer === 'string' ? vis.footer : ''}</div>}
    </div>
  );
}

export default ReportDocument;
